"use client";

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Save, FileText, Loader2 } from 'lucide-react';

interface UserNotesTabProps {
  userId: string;
  initialNotes?: string;
  onUpdate: () => void;
}

export function UserNotesTab({ userId, initialNotes, onUpdate }: UserNotesTabProps) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setHasChanges(value !== (initialNotes || ''));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const { error } = await supabase.rpc('update_user_admin_notes' as any, {
        p_user_id: userId,
        p_notes: notes
      });

      if (error) throw error;

      toast({
        title: "Notes saved",
        description: "Admin notes have been updated successfully.",
      });
      
      setHasChanges(false);
      onUpdate();
    } catch (err: any) {
      console.error('Failed to save notes:', err);
      toast({
        title: "Error",
        description: "Failed to save notes: " + err.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Admin Notes
        </CardTitle>
        <CardDescription>
          Internal notes about this user. Only visible to administrators.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          placeholder="Add notes about this user..."
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          rows={6}
          className="resize-none"
        />
        
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
          </p>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Notes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
